import { BaseLayouter } from './BaseLayouter';
import { LayoutData } from '../LayoutData';
import { POI } from '../../poi/POI';
import { Vector3 } from '../../types/Vector3';
import * as THREE from 'three';

/**
 * TrackLayouter
 * - Respects explicit positions (no-op placement)
 * - Exists to provide a named layouter that can later expose track/path context
 */
export class TrackLayouter extends BaseLayouter {
  private _curve: THREE.CatmullRomCurve3 | null = null;
  private _alignToPath: boolean = false; // configurable later via LayoutData if needed

  constructor() {
    super(
      'track',
      'Track layouter (preserves imported positions; optional path alignment)',
      {},
      '1d',
      []
    );
  }

  // No-op: preserve current positions
  applyToObjects(pois: POI[], _layoutData: LayoutData): void {
    if (!pois || pois.length === 0) return;

    // Lazy morphlineJson build if missing and ARTrack hint present
    try {
      const sceneSettings = pois[0].getSceneSettings?.();
      if (sceneSettings && !sceneSettings.morphlineJson.value && sceneSettings.artrackTrackId.value) {
        // Fire and forget lazy load; do not block layout
        this.lazyFetchAndBuildPath(sceneSettings, pois).catch(() => {});
      }
    } catch {}

    // If morphlineJson exists, assign positions from it (runtime projection)
    let points: THREE.Vector3[] | null = null;
    try {
      const sceneSettings = pois[0].getSceneSettings?.();
      const json = sceneSettings?.morphlineJson?.value;
      if (json && typeof json === 'string' && json.trim().length > 0) {
        const arr: Array<{ x: number; y: number; z: number }> = JSON.parse(json);
        if (Array.isArray(arr) && arr.length > 0) {
          points = new Array(pois.length);
          for (let i = 0; i < pois.length; i++) {
            const idx = Math.min(i, arr.length - 1);
            const p = arr[idx];
            pois[i].poidata.position.value = new Vector3(p.x || 0, p.y || 0, p.z || 0);
            points[i] = new THREE.Vector3(p.x || 0, p.y || 0, p.z || 0);
          }
        }
      }
    } catch {}

    // Build path curve from current or assigned positions
    if (!points) {
      points = pois.map(p => new THREE.Vector3(
        p.poidata.position.value?.x ?? 0,
        p.poidata.position.value?.y ?? 0,
        p.poidata.position.value?.z ?? 0
      ));
    }
    if (points.length >= 2) {
      this._curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.1);
    } else {
      this._curve = null;
    }

    if (!this._alignToPath || !this._curve) {
      return; // keep positions/orientations as-is
    }

    // Optional: orient labels/shapes to face along path tangent
    const tmp = new THREE.Vector3();
    for (let i = 0; i < pois.length; i++) {
      const t = i / Math.max(1, pois.length - 1);
      const tangent = this._curve.getTangent(t, tmp).normalize();
      // Convert tangent to yaw-only Euler (Y-up): assume forward = +Z
      const yaw = Math.atan2(tangent.x, tangent.z);
      const rot = pois[i].poidata.rotation.value || Vector3.zero();
      pois[i].poidata.rotation.value = new Vector3(0, yaw * (180 / Math.PI), 0);
    }
  }

  private async lazyFetchAndBuildPath(sceneSettings: any, pois: POI[]) {
    try {
      const trackIdNum = Number(sceneSettings.artrackTrackId.value);
      if (!trackIdNum || isNaN(trackIdNum)) return;
      const useUTM = !!sceneSettings.artrackUseUTM.value;
      const utmZone = sceneSettings.artrackUtmZone.value || undefined;
      const altitudeScale = Number(sceneSettings.artrackAltitudeScale.value) || 1.0;
      const trackScale = Number(sceneSettings.artrackTrackScale.value) || 1.0;
      console.log(
        `🛤️ TrackLayouter: Lazy loading track`,
        { trackId: trackIdNum, useUTM, utmZone, altitudeScale, trackScale }
      );
      const { trackAdapter } = await import('../../../integrations/tracks/trackAdapter');
      const { UTMProjection } = await import('../../../integrations/tracks/UTMProjection');
      const { TracksFactory } = await import('../../../integrations/tracks/TracksFactory');
      const projection = useUTM ? new UTMProjection({ utmZone, altitudeScale, trackScale }) : undefined;
      const appSettings = pois[0].getAppSettings?.();
      if (!appSettings) return;
      const factory = new TracksFactory(sceneSettings, appSettings);
      const { pois: generatedPois } = await factory.createFromTrack(trackIdNum, { projection });
      //console.log(`🛤️ TrackLayouter: Generated POIs from track`, { count: generatedPois.length });
      const pathPoints = generatedPois.map(p => ({ x: p.poidata.position.value?.x ?? 0, y: p.poidata.position.value?.y ?? 0, z: p.poidata.position.value?.z ?? 0 }));
      sceneSettings.morphlineJson.value = JSON.stringify(pathPoints);
      try {
        const preview = pathPoints.slice(0, 5);
        //console.log(`🛤️ TrackLayouter: morphlineJson set`, { points: pathPoints.length, preview });
        (window as any).morphlineJson = sceneSettings.morphlineJson.value;
        localStorage.setItem('3dp:last-morphline-json', sceneSettings.morphlineJson.value);
      } catch {}
      // Attach ARTrack runtime meta onto existing POIs by index if available
      const count = Math.min(pois.length, generatedPois.length);
      for (let i = 0; i < count; i++) {
        const meta = (generatedPois[i] as any)?.poidata?.artrackMeta;
        if (meta) {
          (pois[i] as any).poidata.artrackMeta = meta;
        }
        const genUri = (generatedPois[i] as any)?.poidata?.uri?.value;
        if (Array.isArray(genUri) && genUri.length > 0) {
          (pois[i] as any).poidata.uri.value = genUri;
        }
        const genWpId = (generatedPois[i] as any)?.poidata?.artrack_waypointId?.value;
        if (!Number.isNaN(genWpId) && genWpId != null) {
          (pois[i] as any).poidata.artrack_waypointId.value = genWpId;
        }
        // Assign positions from generated POIs immediately
        const gp = (generatedPois[i] as any)?.poidata?.position?.value;
        if (gp) {
          (pois[i] as any).poidata.position.value = new Vector3(gp.x || 0, gp.y || 0, gp.z || 0);
        }
      }
      console.log('🛤️ TrackLayouter: Lazy load complete and positions assigned.');
    } catch {}
  }

  // Fallback methods (unused when applyToObjects is present)
  calculatePosition(_index: number, _totalCount: number, _layoutData: LayoutData): Vector3 {
    return Vector3.zero();
  }

  calculateOrientation(_index: number, _totalCount: number, _layoutData: LayoutData): Vector3 {
    return new Vector3(0, 0, 0);
  }

  protected calculateOrientationQuaternion(
    _position: Vector3,
    _index: number,
    _totalCount: number,
    _layoutData: LayoutData
  ): import('../../types/Quaternion').Quaternion {
    // Identity rotation (preserve existing)
    const { Quaternion } = require('../../types/Quaternion');
    return new Quaternion();
  }

  protected getFixedSpacingParameter(_layoutData: LayoutData): number {
    return 1.0;
  }

  protected calculateUniformSpacing(_layoutData: LayoutData, _objectCount: number): number {
    return 1.0;
  }
}

